import {
  Button,
  OutlinedSelect,
  SearchAccordion,
  SearchBox,
  SelectOptionType,
  useMobile,
} from '@octano/global-ui';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Row,
} from 'reactstrap';

import { TranslationsKeys } from '../../../../locales/translations';
import { useSearchFiltersLoaderData } from '../hooks/SearchFiltersLoader';

export type Filters = {
  name: string | null;
  code: string | null;
  school: SelectOptionType | null;
  modality: SelectOptionType | null;
  campus: SelectOptionType | null;
};

const DEFAULT_VALUES = {
  name: null,
  code: null,
  school: null,
  modality: null,
  campus: null,
};

interface SearchFiltersProps {
  onFiltersChange: (filterValues: Filters) => void;
}

interface CreateHandlers {
  onCreateCourse?: () => void;
  onCreateProgram?: () => void;
}

interface ExtendedSearchFiltersProps extends SearchFiltersProps {
  createHandlers?: CreateHandlers;
}

export default function SearchFilters({
  onFiltersChange = () => null,
  createHandlers,
}: ExtendedSearchFiltersProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'searchFilters';
  const isMobile = useMobile();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  // Obtener datos del loader
  const { ...formData } = useSearchFiltersLoaderData();
  const schools = formData?.schools ?? [];
  const modalities = formData?.modalities ?? [];
  const campuses = formData?.campuses ?? [];

  const { handleSubmit, control, reset } = useForm<Filters>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const cleanValues = () => {
    reset();
    onFiltersChange(DEFAULT_VALUES);
  };

  const onSubmit = useCallback(
    async (values: Filters) => {
      onFiltersChange(values);
    },
    [onFiltersChange],
  );

  return (
    <div className="pb-4 pb-lg-1">
      <SearchAccordion btnText={t(`${prefix}.title`)}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="gutters-filterss">
            <Col xs={12} md={6} lg={3}>
              <SearchBox
                name="name"
                label={t(`${prefix}.name`)}
                placeholder={t(`${prefix}.namePlaceholder`)}
                control={control}
              />
            </Col>
            <Col xs={12} md={6} lg={3}>
              <SearchBox
                name="code"
                label={t(`${prefix}.code`)}
                placeholder={t(`${prefix}.codePlaceholder`)}
                control={control}
              />
            </Col>

            <Col xs={12} md={6} lg={3}>
              <OutlinedSelect
                name="campus"
                options={campuses}
                label={t(`${prefix}.campus`)}
                control={control}
              />
            </Col>
            <Col xs={12} md={6} lg={3}>
              <OutlinedSelect
                name="school"
                options={schools}
                label={t(`${prefix}.school`)}
                control={control}
              />
            </Col>
            <Col xs={12} md={6} lg={3}>
              <OutlinedSelect
                name="modality"
                options={modalities}
                label={t(`${prefix}.modality`)}
                control={control}
              />
            </Col>
            <Col xs={12} md={6} lg={3} className="offset-md-6 offset-lg-6">
              <Row
                className="gutters-filters"
                style={{ marginTop: isMobile ? 0 : '1.5rem' }}
              >
                <Col>
                  <Button
                    type="submit"
                    text={t(`${prefix}.search`)}
                    size="md"
                    fullwidth
                  />
                </Col>
                <Col>
                  <Button
                    text={t(`${prefix}.clean`)}
                    size="md"
                    outlined
                    onClick={cleanValues}
                    fullwidth
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
        {createHandlers && (
          <div>
            <Row className="mt-3">
              <Col xs={12} md={6} lg={3} className="offset-md-6 offset-lg-9">
                <Dropdown
                  isOpen={dropdownOpen}
                  toggle={toggle}
                  style={{ width: '100%' }}
                >
                  <DropdownToggle
                    style={{ width: '100%' }}
                    className="g-button"
                    color="primary"
                    size="md"
                    caret
                  >
                    <span>{t('createButton')}</span>
                  </DropdownToggle>
                  <DropdownMenu style={{ minWidth: '100%' }}>
                    <DropdownItem onClick={createHandlers.onCreateCourse}>
                      {t('createOptions.course')}
                    </DropdownItem>
                    <DropdownItem onClick={createHandlers.onCreateProgram}>
                      {t('createOptions.program')}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </Col>
            </Row>
          </div>
        )}
      </SearchAccordion>
    </div>
  );
}
